<template>
  <div v-if="trustedIframeSrc !== 'about:blank'" class="hitphone-root">
    <!-- Bottom bar -->
    <div
      class="fixed bottom-0 left-0 right-0 w-full border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 h-12 flex items-stretch justify-end z-40 hitphone-bar-shadow"
      role="contentinfo"
      aria-label="HIT Phone bar"
    >
      <button
        type="button"
        class="inline-flex items-center gap-2 px-3 h-full text-slate-800 dark:text-slate-100 focus:outline-none transition-colors"
        :class="
          isOpen
            ? 'bg-slate-100 dark:bg-slate-700 border-b-2 border-b-primary-500'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
        "
        :aria-expanded="isOpen ? 'true' : 'false'"
        aria-controls="hitphone-panel"
        title="HIT Phone"
        @click="toggleWidget"
      >
        <fluent-icon
          icon="call"
          size="18"
          class="text-slate-800 dark:text-slate-100"
        />
        <span class="text-sm font-bold">HIT Phone</span>
      </button>
    </div>

    <!-- Widget panel -->
    <div
      id="hitphone-panel"
      class="fixed right-[14px] bottom-12 z-40 transition-all duration-200 ease-out"
      :class="{
        'opacity-100 translate-y-0': isOpen,
        'opacity-0 translate-y-2 pointer-events-none': !isOpen,
      }"
      :style="panelStyle"
      role="dialog"
      aria-label="HIT Phone panel"
      aria-hidden="!isOpen"
    >
      <div
        class="bg-white dark:bg-slate-900 border-l border-r border-t border-slate-200 dark:border-slate-700"
      >
        <!-- Header bar -->
        <div
          class="h-[46px] flex items-center justify-between px-3 border-b-2 border-slate-300 dark:border-slate-600 cursor-pointer"
          title="Minimize"
          @click="closeWidget"
        >
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="call"
              size="18"
              class="text-slate-800 dark:text-slate-100"
            />
            <span class="font-medium text-slate-800 dark:text-slate-100">
              HIT Phone
            </span>
          </div>
          <fluent-icon
            icon="subtract"
            size="18"
            type="solid"
            class="text-slate-600 dark:text-slate-400"
          />
        </div>

        <!-- iframe content -->
        <iframe
          ref="iframeEl"
          :src="trustedIframeSrc"
          title="HIT Phone"
          class="block"
          style="width: 305px; height: 450px"
          frameborder="0"
          loading="eager"
          allow="microphone; autoplay"
          sandbox="allow-scripts allow-same-origin"
          referrerpolicy="no-referrer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'HitPhoneWidget',
  props: {
    iframeSrc: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
      allowedOrigin: null,
      isHitPhoneReady: false,
      pendingCall: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUserId: 'getCurrentUserID',
    }),
    trustedIframeSrc() {
      try {
        const url = new URL(this.iframeSrc);
        return url.toString();
      } catch (e) {
        return 'about:blank';
      }
    },
    panelStyle() {
      return { width: '305px' };
    },
  },
  watch: {
    iframeSrc: {
      immediate: true,
      handler(newVal) {
        this.setAllowedOriginFromSrc(newVal);
      },
    },
  },
  created() {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.onMessage, false);
    }
    this.setAllowedOriginFromSrc(this.iframeSrc);
    this.$emitter.on('hitphone:logout', this.sendUserLoggedOutEvent);
    this.$emitter.on('hitphone:call', this.enqueueCall);
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.onMessage, false);
    }
    this.$emitter.off('hitphone:logout', this.sendUserLoggedOutEvent);
    this.$emitter.off('hitphone:call', this.enqueueCall);
  },
  methods: {
    setAllowedOriginFromSrc(src) {
      try {
        const url = new URL(src);
        this.allowedOrigin = url.origin;
      } catch (e) {
        this.allowedOrigin = null;
      }
    },
    enqueueCall(payload) {
      const phone = payload?.phone;
      if (!phone) return;

      this.openWidget();
      this.pendingCall = { phone };
      this.flushPendingCallIfPossible();
    },
    flushPendingCallIfPossible() {
      if (!this.pendingCall?.phone) return;
      if (!this.allowedOrigin) return;
      if (!this.isHitPhoneReady) return;

      const iframeWindow =
        this.$refs.iframeEl && this.$refs.iframeEl.contentWindow;
      if (!iframeWindow) return;

      const phone = this.pendingCall.phone;
      iframeWindow.postMessage(
        {
          type: 'hitphone.call',
          number: phone,
        },
        this.allowedOrigin
      );

      this.pendingCall = null;

      const currentQuery = this.$route?.query || {};
      if (
        currentQuery.action === 'call' &&
        String(currentQuery.phone || '') === String(phone)
      ) {
        const query = { ...currentQuery };
        delete query.action;
        delete query.phone;
        this.$router.replace({ query });
      }
    },
    toggleWidget() {
      this.isOpen = !this.isOpen;
    },
    openWidget() {
      this.isOpen = true;
    },
    closeWidget() {
      this.isOpen = false;
    },
    openUrl(url, name) {
      if (!url || typeof url !== 'string') {
        return;
      }
      try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          return;
        }
        const targetName = name && typeof name === 'string' ? name : '_blank';
        const win = window.open(url, targetName);
        if (win) {
          try {
            win.focus();
          } catch (_) {
            // Focus may fail due to popup blockers or cross-origin restrictions
          }
        }
      } catch (_) {
        // Invalid URL, ignore
      }
    },
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts.pop().split(';').shift() || null;
      }
      return null;
    },
    onMessage(event) {
      if (!event || !event.data || typeof event.data !== 'object') {
        return;
      }
      if (!this.allowedOrigin || event.origin !== this.allowedOrigin) {
        return;
      }
      const iframeWindow =
        this.$refs.iframeEl && this.$refs.iframeEl.contentWindow;
      if (iframeWindow && event.source !== iframeWindow) {
        return;
      }
      const { type, url, name } = event.data;
      if (type === 'hitphone.extensionReady') {
        this.isHitPhoneReady = true;
        this.flushPendingCallIfPossible();
      } else if (type === 'hitphone.openWidget') {
        this.openWidget();
      } else if (type === 'hitphone.closeWidget') {
        this.closeWidget();
      } else if (type === 'hitphone.toggleWidget') {
        this.toggleWidget();
      } else if (type === 'hitphone.openUrl') {
        this.openUrl(url, name);
      } else if (type === 'hitphone.getCookie') {
        this.handleCookieRequest(event);
      } else if (type === 'hitphone.getCurrentUserId') {
        this.handleUserIdRequest(event);
      }
    },
    handleCookieRequest(event) {
      if (!this.allowedOrigin || event.origin !== this.allowedOrigin) {
        return;
      }

      const iframeWindow =
        this.$refs.iframeEl && this.$refs.iframeEl.contentWindow;
      if (!iframeWindow || event.source !== iframeWindow) {
        return;
      }

      const cookieValue = this.getCookie('cw_d_session_info');

      iframeWindow.postMessage(
        {
          type: 'hitphone.cookieResponse',
          cookie: cookieValue,
        },
        this.allowedOrigin
      );
    },
    handleUserIdRequest(event) {
      if (!this.allowedOrigin || event.origin !== this.allowedOrigin) {
        return;
      }

      const iframeWindow =
        this.$refs.iframeEl && this.$refs.iframeEl.contentWindow;
      if (!iframeWindow || event.source !== iframeWindow) {
        return;
      }

      iframeWindow.postMessage(
        {
          type: 'hitphone.userIdResponse',
          userId: this.currentUserId,
        },
        this.allowedOrigin
      );
    },
    sendUserLoggedOutEvent() {
      if (!this.allowedOrigin) {
        return;
      }

      const iframeWindow =
        this.$refs.iframeEl && this.$refs.iframeEl.contentWindow;
      if (!iframeWindow) {
        return;
      }

      iframeWindow.postMessage(
        {
          type: 'hitphone.userLoggedOut',
        },
        this.allowedOrigin
      );
    },
  },
};
</script>

<style scoped>
.hitphone-bar-shadow {
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
</style>
